<template>
  <div>
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>
    
    <gd-modal
      id="user-access-modal"
      name="user-access-modal"
      :title="modalTitle"
      @closed="accessMessageInitialized()"
    >
      <div class="p-6">
        <div class="flex flex-col mb-2">
          <label class="font-semibold text-sm text-grey-700 mb-2">
            {{ $t('frontend_groovemail_admin_access_block_label') }}
          </label>
          <text-area
            variant="base"
            v-model="model.accessBlockReason"
            :placeholder="$t('frontend_groovemail_admin_access_block_reason')"
            rows="5"
          />
          <p
            class="w-full error text-danger mt-2 text-sm"
            v-if="this.formSubmitted && !$v.model.accessBlockReason.required"
          >
            {{ $t('frontend_groovemail_admin_access_block_reason_required') }}
          </p>
          <p
            class="w-full error text-danger mt-2 text-sm"
            v-if="this.formSubmitted && !$v.model.accessBlockReason.maxLength"
          >
            {{
              `${$t(
                'frontend_groovemail_admin_access_block_reason_must_have_at_most'
              )} ${$v.model.accessBlockReason.$params.maxLength.max}  ${$t(
                'frontend_groovemail_admin_access_block_reason_characters'
              )}.`
            }}
          </p>
        </div>
        <div class="flex flex-col mb-6">
          <label class="font-semibold text-sm text-grey-700 mb-2">
            {{ $t('frontend_groovemail_admin_access_block_by_user') }}
          </label>
          <text-input
            variant="base"
            v-model="model.accessBlockByUser"
            :placeholder="this.$t('frontend_groovemail_admin_access_block_by_user')"
          />
          <p
            class="w-full error text-danger mt-2 text-sm"
            v-if="this.formSubmitted && !$v.model.accessBlockByUser.required"
          >
            {{
              $t(
                'frontend_groovemail_admin_admin_access_block_by_user_required'
              )
            }}
          </p>
        </div>
        <div class="flex justify-end">
          <gd-button
            size="auto"
            @click="closeAccessMessageModal"
            >{{ $t('frontend_cancel') }}</gd-button
          >
          <gd-button
            variant="primary"
            size="auto"
            class="ml-2"
            @click="updateUserAccess()"
          >
            {{ $t('frontend_save') }}</gd-button
          >
        </div>
      </div>
    </gd-modal>
  </div>
</template>

<script>
import axios from 'axios';
import themeConfig from '@/theme/themeConfig.js';
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import Swal from 'sweetalert2';
import showAlert from '@/components/sweet-alert-popup.js';

export default {
  data() {
    return {
      model: {
        accessBlockReason: '',
        accessBlockByUser: '',
      },
      formSubmitted: false,
      userId: '',
      type: '',
      fullPageLoader: true,
      isLoading: false,
      loaderColor: themeConfig.appPrimaryColor,
    };
  },
  validations: {
    model: {
      accessBlockReason: {
        required,
        minLength: minLength(3),
        maxLength: maxLength(500),
      },
      accessBlockByUser: {
        required,
      },
    },
  },
  computed: {
    modalTitle() {
      return this.type === 'smtp'
        ? this.$t('frontend_groovemail_admin_user_smtp_modal_label')
        : this.$t('frontend_groovemail_admin_user_access_modal_label');
    },
  },
  methods: {
    async updateUserAccess() {
      this.$data.formSubmitted = true;
      this.$v.model.accessBlockReason.$touch();
      this.$v.model.accessBlockByUser.$touch();
      if (
        !this.$v.model.accessBlockReason.$invalid &&
        !this.$v.model.accessBlockByUser.$invalid
      ) {
        const confirmEdit = await Swal.fire({
          html: `
            <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
            <p class="text-lg font-bold text-danger mb-3"> Please type CONFIRM to confirm your changes</p>
          `,
          title: this.$t('frontend_groovemail_admin_confirmation_popup'),
          input: 'text',
          showCancelButton: true,
          showCloseButton: true,
          confirmButtonColor: '#2f80ed',
          cancelButtonColor: themeConfig.appPrimaryColor,
          cancelButtonText: 'Cancel',
          confirmButtonText: 'Confirm',
          inputValidator: (value) => {
            if (value !== 'CONFIRM') {
              return 'Please enter correct message to confirm your changes';
            }
          },
        });
        if (
          confirmEdit.dismiss === Swal.DismissReason.cancel ||
          confirmEdit.dismiss === Swal.DismissReason.close
        ) {
          return false;
        } else {
          this.showLoader();
          try {
            await axios.post('/admin/block-user-reason', {
              block_reason: this.model.accessBlockReason,
              block_by_user: this.model.accessBlockByUser,
              type: this.type,
              owner_id: this.userId,
            });
            this.hideLoader();
            this.closeAccessMessageModal();
          } catch (e) {
            this.hideLoader();
            if (e.response.status === 422) {
              this.showErrorMessage(e?.response?.data?.message);
            } else {
              this.showErrorMessage(e?.response?.data?.message);
            }
          }
        }
        //   this.$refs.serverTable.$refs.formsTable.refresh();
        this.formTableRefresh();
      }
    },
    async enableUserAccess(userId, type) {
      const confirmEdit = await Swal.fire({
        html: `
          <style>.swal2-cancel { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-lg font-bold text-danger mb-3"> Please type CONFIRM to confirm your changes</p>
        `,
        title: `${this.$t('frontend_groovemail_admin_enable_confirmation')} ${
          type == 'smtp'
            ? this.$t('frontend_groovemail_admin_enable_smtp_confirmation')
            : this.$t('frontend_groovemail_admin_enable_access_confirmation')
        }`,
        input: 'text',
        showCancelButton: true,
        showCloseButton: true,
        confirmButtonColor: '#2f80ed',
        cancelButtonColor: themeConfig.appPrimaryColor,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Confirm',
        inputValidator: (value) => {
          if (value !== 'CONFIRM') {
            return 'Please enter correct message to confirm your changes';
          }
        },
      });
      if (
        confirmEdit.dismiss === Swal.DismissReason.cancel ||
        confirmEdit.dismiss === Swal.DismissReason.close
      ) {
        return false;
      } else {
        console.log(userId, type);
        this.showLoader();
        try {
          await axios.post('/admin/unblock-user-reason', {
            owner_id: userId,
            type: type,
          });
          this.hideLoader();
        } catch (e) {
          this.hideLoader();
          console.log(e);
          this.showErrorMessage(e?.response?.data?.message);
        }
        this.formTableRefresh();
        // this.$refs.serverTable.$refs.formsTable.refresh();
        
      }
    },
    async editUserAccess(id, type) {
      this.userId = id;
      this.type = type;
      this.model.type = type;
      (this.formSubmitted = false), this.openAccessMessageModal();
    },
    openAccessMessageModal() {
      this.$root.$emit('modal-open', 'user-access-modal');
    },
    closeAccessMessageModal() {
      this.$root.$emit('modal-close', 'user-access-modal');
    },
    accessMessageInitialized() {
      this.model.type = '';
      (this.formSubmitted = false), (this.modalType = 'add');
      this.$v.model.accessBlockReason.$reset();
      this.$v.model.accessBlockByUser.$reset();
    },
    showErrorMessage(errorMessage) {
      showAlert({
        iconType: 'alert-error',
        html: `
          <style>.swal2-confirm { color: ${themeConfig.appPrimaryColorInvert} !important; }</style>
          <p class="text-sm text-deep-dark"> ${this.$t(errorMessage)}</p>
        `,
        showCancelButton: false,
        showCloseButton: true,
        confirmButtonColor: themeConfig.appPrimaryColor,
        confirmButtonText: this.$t('frontend_close'),
      });
    },
    formTableRefresh() {
      this.$emit('refreshTable');
    },
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>
